import Swal from 'sweetalert2';

/**
 * Configuración por defecto para SweetAlert2
 */
const defaultConfig = {
  confirmButtonColor: '#3B82F6', // primary-600
  cancelButtonColor: '#6B7280', // secondary-500
  confirmButtonText: 'Aceptar',
  cancelButtonText: 'Cancelar',
};

/**
 * Muestra un alert de éxito
 */
export const showSuccess = (title: string, message?: string) => {
  return Swal.fire({
    icon: 'success',
    title,
    text: message,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
  });
};

/**
 * Muestra un alert de error
 */
export const showError = (title: string, message?: string) => {
  return Swal.fire({
    icon: 'error',
    title,
    text: message,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
  });
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Muestra un alert de error con una lista de mensajes (ej. errores de
 * validación), cada uno en su propio párrafo — a diferencia de showError()
 * con text plano, aquí cada mensaje queda en su propia línea/div en vez de
 * corrido en un solo bloque de texto.
 */
export const showErrorList = (title: string, errors: string[]) => {
  const html = errors
    .map((err) => `<div style="text-align:left;margin-bottom:6px;">${escapeHtml(err)}</div>`)
    .join('');

  return Swal.fire({
    icon: 'error',
    title,
    html,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
  });
};

/**
 * Aviso NO bloqueante a los 30s de espera del reporte Balance General: no
 * cancela la descarga sincrona en curso, solo ofrece la alternativa de
 * correo. Devuelve { isConfirmed: true } si el usuario elige correo,
 * { isConfirmed: false } si elige seguir esperando (o cierra el dialogo).
 */
export const showBalanceSheetWaitNotice = () => {
  return Swal.fire({
    icon: 'info',
    title: 'El reporte esta tardando mas de lo esperado',
    text: 'Puedes seguir esperando o te lo enviamos por correo cuando este listo.',
    showCancelButton: true,
    confirmButtonText: 'Enviar por correo',
    cancelButtonText: 'Seguir esperando',
    confirmButtonColor: defaultConfig.confirmButtonColor,
    cancelButtonColor: defaultConfig.cancelButtonColor,
    allowOutsideClick: true,
  });
};

/**
 * Modal de 3 opciones cuando la descarga sincrona del reporte falla antes
 * del corte de 90s (status distinto de 200, respuesta invalida/corrupta):
 * reintentar, enviar por correo, o cancelar -- mismo patron que el modal
 * handleRetry del portal legado.
 */
export const showReportRetryOrEmailPrompt = (message: string) => {
  return Swal.fire({
    icon: 'error',
    title: 'No fue posible descargar el reporte',
    text: message,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Reintentar',
    denyButtonText: 'Enviar por correo',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: defaultConfig.confirmButtonColor,
    denyButtonColor: '#0d9488',
    cancelButtonColor: defaultConfig.cancelButtonColor,
  });
};

/**
 * Muestra un alert de advertencia
 */
export const showWarning = (title: string, message?: string) => {
  return Swal.fire({
    icon: 'warning',
    title,
    text: message,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
  });
};

/**
 * Muestra un alert de información
 */
export const showInfo = (title: string, message?: string) => {
  return Swal.fire({
    icon: 'info',
    title,
    text: message,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
  });
};

/**
 * Muestra un diálogo de confirmación
 */
export const showConfirm = (title: string, message?: string) => {
  return Swal.fire({
    icon: 'question',
    title,
    text: message,
    showCancelButton: true,
    confirmButtonColor: defaultConfig.confirmButtonColor,
    cancelButtonColor: defaultConfig.cancelButtonColor,
    confirmButtonText: defaultConfig.confirmButtonText,
    cancelButtonText: defaultConfig.cancelButtonText,
  });
};

/**
 * Muestra un loading/spinner
 */
export const showLoading = (title: string, message?: string) => {
  return Swal.fire({
    title,
    text: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });
};

/**
 * Cierra el SweetAlert actual
 */
export const closeAlert = () => {
  Swal.close();
};

/**
 * Muestra un toast (notificación pequeña)
 */
export const showToast = (icon: 'success' | 'error' | 'warning' | 'info', title: string) => {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });

  return Toast.fire({
    icon,
    title,
  });
};
